import { ProductDraft } from "@/types/general";
import {
    flattenPaginationRequest,
    internalApiSlice,
    PaginationRequest,
    PaginationResponse,
    transformResponse,
} from "@redux/api/internalApiSlice";

const productDraftDateFields = ["created_at", "updated_at"];
const productDraftListTag = { type: "ProductDraft" as const, id: "LIST" };

function createProductDraftUpdateFormData(update: ProductDraft.DTO.UpdateInformation) {
    const { image, ...payload } = update;
    const formData = new FormData();

    formData.append("_method", "PUT");
    formData.append(
        "payload",
        JSON.stringify(image === null ? { ...payload, image: null } : payload)
    );

    if (image instanceof File) {
        formData.append("image", image);
    }

    return formData;
}

export const productDraftsApi = internalApiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getProductDrafts: builder.query<
            PaginationResponse<ProductDraft.Root>,
            PaginationRequest<ProductDraft.DTO.Filters, ProductDraft.DTO.OrderBy>
        >({
            query: (request) => ({
                url: "product-draft",
                method: "GET",
                params: flattenPaginationRequest(request),
                parseDates: productDraftDateFields,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.payload.map(({ id }) => ({
                              type: "ProductDraft" as const,
                              id,
                          })),
                          productDraftListTag,
                      ]
                    : [productDraftListTag],
        }),

        getProductDraft: builder.query<ProductDraft.Extended, number>({
            query: (id) => ({
                url: `product-draft/${id}`,
                method: "GET",
                parseDates: productDraftDateFields,
            }),
            transformResponse,
            providesTags: (_, __, id) => [{ type: "ProductDraft", id }],
        }),

        updateProductDraftInformation: builder.mutation<
            ProductDraft.Extended,
            ProductDraft.DTO.UpdateInformation & { draft_id: number }
        >({
            query: ({ draft_id, ...update }) => ({
                url: `product-draft/${draft_id}`,
                method: "POST",
                data: createProductDraftUpdateFormData(update),
                parseDates: productDraftDateFields,
            }),
            transformResponse,
            invalidatesTags: (_, __, { draft_id }) => [
                { type: "ProductDraft", id: draft_id },
                productDraftListTag,
            ],
        }),

        deleteProductDraft: builder.mutation<void, number>({
            query: (id) => ({
                url: `product-draft/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: (_, __, id) => [{ type: "ProductDraft", id }, productDraftListTag],
        }),

        importProductDrafts: builder.mutation<void, FormData>({
            query: (formData) => ({
                url: "product-draft/import",
                method: "POST",
                data: formData,
            }),
        }),

        materializeProductDrafts: builder.mutation<void, ProductDraft.DTO.Materialize>({
            query: (data) => ({
                url: "product-draft/export",
                method: "POST",
                data,
            }),
        }),

        exportProductDraftsFile: builder.mutation<void, ProductDraft.DTO.ExportFile>({
            query: (data) => ({
                url: "product-draft/export-file",
                method: "POST",
                responseType: "blob",
                data,
            }),
        }),
    }),
});

export const {
    useGetProductDraftsQuery,
    useGetProductDraftQuery,
    useUpdateProductDraftInformationMutation,
    useDeleteProductDraftMutation,
    useImportProductDraftsMutation,
    useMaterializeProductDraftsMutation,
    useExportProductDraftsFileMutation,
} = productDraftsApi;
